import { useState, useEffect, useRef } from 'react';

import { appBorders, appColors } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ChatProfilePicture } from '@backrs_ui/grow-ui/src/components/Lubav/ChatProfilePicture';
import {
  EditMessageBox,
  UserMessageActionsButton,
} from '@backrs_ui/grow-ui/src/components/Lubav/EditMessageBox';
import { FileUpload } from '@backrs_ui/grow-ui/src/components/Lubav/FileUpload';
import {
  FileUploadError,
  getUploadDocumentMessage,
} from '@backrs_ui/grow-ui/src/components/Lubav/fileUpload.util';
import { SelectedUser } from '@backrs_ui/grow-ui/src/components/Lubav/SelectedUser';
import {
  RoleForPrompts,
  SuggestedPrompts,
} from '@backrs_ui/grow-ui/src/components/Lubav/SuggestedPrompts';
import { TypingIndicator } from '@backrs_ui/grow-ui/src/components/Lubav/TypingIndicator';
import { UserSearch, UserSelect } from '@backrs_ui/grow-ui/src/components/Lubav/UserSearch';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  ResourcesContent,
  OfficeHoursMessage,
  SENDER_TYPE,
  OfficeHoursSessionType,
  DocumentType,
  OFFICE_HOURS_SESSION_TYPES,
  LubavSessionType,
  LubavSupersession,
  LUBAV_SUPERSESSIONS,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  setIsLoadingResources,
  setCurrentResponseId,
  setResourcesContent,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { fetchProfileImages } from '@backrs_ui/grow-ui/src/redux/boxData/thunks/boxData.thunk';
import {
  addOfficeHoursMessage,
  setOfficeHoursMessages,
  updateLastMessage,
  setSuggestedPrompts,
  resetSuggestedPrompts,
  setSessionId,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { incrementCostsThunk } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
  createSessionAction,
  typeGuardForOfficeHoursSessionType,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { checkForUserDocument } from '@backrs_ui/grow-ui/src/utils/checkForUserDocument';
import {
  fetchStreamedLubavMessage,
  StreamedMessage,
} from '@backrs_ui/grow-ui/src/utils/streamUtils';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { Box, IconButton, SxProps, Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { InputBox } from './InputBox';
import { SingleSelect } from './SingleSelect';
const { chatBox } = appStrings.modules;

type AIMessageActionsButtonProps = Readonly<{
  text: string;
}>;

const AIMessageActionsButton = ({ text }: AIMessageActionsButtonProps) => {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(chatBox.copyErr, err);
    }
  };

  const handleThumbsUp = () => {
    setFeedback((prev) => (prev === 'up' ? null : 'up'));
    // TODO: add thumbs up logic
  };

  const handleThumbsDown = () => {
    setFeedback((prev) => (prev === 'down' ? null : 'down'));
    // TODO: add thumbs down logic
  };

  return (
    <Box sx={messageActionsContainer}>
      <IconButton onClick={copyToClipboard} sx={buttonStyle}>
        <ContentCopyIcon style={iconStyle} />
      </IconButton>
      <IconButton onClick={handleThumbsUp} sx={buttonStyle}>
        {feedback === 'up' ? (
          <ThumbUpIcon style={activeIconStyle} />
        ) : (
          <ThumbUpOutlinedIcon style={iconStyle} />
        )}
      </IconButton>
      <IconButton onClick={handleThumbsDown} sx={buttonStyle}>
        {feedback === 'down' ? (
          <ThumbDownIcon style={activeIconStyle} />
        ) : (
          <ThumbDownOutlinedIcon style={iconStyle} />
        )}
      </IconButton>
    </Box>
  );
};

type ChatBoxProps = Readonly<{
  superuser: boolean;
  supercoach: boolean;
}>;

const getRoleForPrompts = (
  isSupercoach: boolean,
  isSuperuser: boolean,
  isGuidanceCounselor: boolean,
  activeSession: LubavSessionType | null,
): RoleForPrompts => {
  if (!activeSession) return 'default';
  if (isSupercoach) return 'supercoach';
  if (isSuperuser || typeGuardForOfficeHoursSessionType(activeSession)) return 'no-default';
  if (isGuidanceCounselor) return 'guidance_counselor';

  return 'default';
};

const MarkdownMessage = ({ children }: { children: string }) => (
  <ReactMarkdown
    components={{
      a: ({ ...props }) => (
        <a style={linkStyle} target="_blank" rel="noopener noreferrer" {...props} href={props.href}>
          {props.children}
        </a>
      ),
    }}
  >
    {children}
  </ReactMarkdown>
);

const SESSION_TYPE_DOCUMENT_REQUIREMENTS: Record<
  OfficeHoursSessionType | LubavSupersession,
  DocumentType | undefined
> = {
  [OFFICE_HOURS_SESSION_TYPES.BOOST_YOUR_PROFILE]: 'resume',
  [OFFICE_HOURS_SESSION_TYPES.BOOST_ODDS_OF_AN_OFFER]: 'resume',
  [OFFICE_HOURS_SESSION_TYPES.RESUME_OPTIMIZER]: 'resume',
  [OFFICE_HOURS_SESSION_TYPES.ESSAY_IDEAS]: undefined,
  [OFFICE_HOURS_SESSION_TYPES.WHAT_ARE_YOUR_CHANCES]: 'resume',
  [LUBAV_SUPERSESSIONS.SUPERCOACH]: undefined,
  [LUBAV_SUPERSESSIONS.SUPERUSER]: undefined,
};

export const ChatBox = ({ superuser, supercoach }: ChatBoxProps) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);

  const isLoadingResources = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData.loading,
  );

  const {
    coachingStyle,
    language,
    openAIModel: LLM,
    officeHoursMessages,
    coachingUser,
    suggestedPrompts,
  } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const {
    currentResponseId,
    boxData: {
      mainResponse: { resources: resourcesContent },
    },
  } = useAppSelector((state) => getSessionState(state, activeSession).boxData);
  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const isGuidanceCounselor = useAppSelector((state) => state.user.isGuidanceCounselor);
  const [gotSessionResponse, setGotSessionResponse] = useState(false);
  const [userHasConfirmedDocument, setUserHasConfirmedDocument] = useState(false);

  const userId = useAppSelector(selectUserId);

  const handleSetResourcesContent = (resourcesContent: ResourcesContent) => {
    dispatch(createSessionAction(setResourcesContent, activeSession)(resourcesContent));

    dispatch(
      fetchProfileImages({
        axios,
        activeSession,
      }),
    );
  };

  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const hasManuallyScrolledRef = useRef(false);

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null); // message being edited
  const [editingText, setEditingText] = useState(''); // text of message being edited

  const [messagePendingDocumentUpload, setMessagePendingDocumentUpload] = useState<
    string | undefined
  >(undefined);

  const asRole: RoleForPrompts = getRoleForPrompts(
    supercoach,
    superuser,
    isGuidanceCounselor,
    activeSession,
  );

  // detects if the user has manually scrolled away from the bottom
  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    const handleScroll = () => {
      const isAtBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 10;

      if (!isAtBottom) {
        hasManuallyScrolledRef.current = true;
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // don't scroll if user has manually scrolled
    if (hasManuallyScrolledRef.current) return;

    const el = scrollRef.current;

    if (!el) return;

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    // scroll to bottom with a slight delay
    scrollToBottom();
    const delay = setTimeout(scrollToBottom, 50);

    return () => {
      clearTimeout(delay);
    };
  }, [officeHoursMessages, isTyping]);

  useEffect(() => {
    if (officeHoursMessages.length === 0 && sessionId && supercoach) {
      dispatch(
        createSessionAction(
          setOfficeHoursMessages,
          activeSession,
        )([
          {
            text: appStrings.modules.supercoach.supercoachWelcome,
            sender: SENDER_TYPE.AI,
          },
        ]),
      );
    }
  }, [officeHoursMessages, supercoach, dispatch, sessionId, activeSession]);

  useEffect(() => {
    const startSession = async () => {
      if (
        officeHoursMessages.length > 0 ||
        !sessionId ||
        !activeSession ||
        supercoach ||
        isTyping ||
        superuser
      )
        return;

      dispatch(createSessionAction(resetSuggestedPrompts, activeSession)(undefined));
      setIsTyping(true);
      try {
        const welcomeMessageResult = await fetchWelcomeMessage(sessionId, activeSession);

        if (!welcomeMessageResult.hasWelcomeMessage) {
          await sendMessage('', '<START>');
        }
      } finally {
        setIsTyping(false);
      }
    };

    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, activeSession, supercoach, userId, language, officeHoursMessages]);

  const fetchWelcomeMessage = async (sessionId: number, activeSession: string) => {
    try {
      const responseId = await fetchStreamedLubavMessage(
        '/api/office-hours/get-welcome-message',
        {
          session_id: sessionId,
          session_type: activeSession,
        },
        (chunk: StreamedMessage) => {
          if (chunk.response) {
            dispatch(
              createSessionAction(
                setOfficeHoursMessages,
                activeSession,
              )([
                {
                  text: chunk.response,
                  sender: SENDER_TYPE.AI,
                  id: chunk.response_id.toString(),
                },
              ]),
            );
            setIsTyping(false);

            if (chunk.suggested_prompts?.length > 0) {
              dispatch(
                createSessionAction(
                  setSuggestedPrompts,
                  activeSession,
                )(chunk.suggested_prompts.map((display: string) => ({ display }))),
              );
            }
          }
        },
      );

      return { hasWelcomeMessage: !!responseId };
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setIsTyping(false);

      return { hasWelcomeMessage: false };
    }
  };

  const handleEditSubmit = async (queryId: string, newText: string) => {
    try {
      setIsTyping(true);
      setEditingMessageId(null);
      const messageIndex = officeHoursMessages.findIndex((msg) => msg.id === queryId);

      const updatedMessages: OfficeHoursMessage[] = [
        ...officeHoursMessages.slice(0, messageIndex),
        { text: newText, sender: SENDER_TYPE.USER, id: queryId },
      ];

      // remove all subsequent messages & temporarily add the new message to the message array
      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(updatedMessages));

      const response = await axios.post('/api/office-hours/get-new-session-id-for-message-edit', {
        session_id: sessionId,
        query_id: queryId,
      });

      const newSessionId = response.data.session_id;

      dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));

      // remove the temporary message, will be replaced by the message with an id in sendMessage
      const messagesWithTempMessageRemoved = updatedMessages.slice(0, messageIndex);

      dispatch(
        createSessionAction(setOfficeHoursMessages, activeSession)(messagesWithTempMessageRemoved),
      );

      await sendMessage(newText, undefined, messagesWithTempMessageRemoved, newSessionId);
      setIsTyping(false);
    } catch (error) {
      console.error('Error editing message:', error);
      setIsTyping(false);
    }
  };

  const handleStreamChunk = (
    chunk: StreamedMessage,
    messagesToUse: OfficeHoursMessage[],
    message: string,
    dispatch: AppDispatch,
    setIsTyping: (value: boolean) => void,
  ) => {
    if (chunk.response && chunk.response.trim()) {
      const updatedMessages: OfficeHoursMessage[] = [
        ...messagesToUse,
        { text: message, sender: SENDER_TYPE.USER, id: chunk.query_id },
        {
          text: chunk.response,
          sender: SENDER_TYPE.AI,
          id: chunk.response_id,
        },
      ];

      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(updatedMessages));
      setIsTyping(false);

      if (chunk.response_id) {
        dispatch(
          createSessionAction(setCurrentResponseId, activeSession)(parseInt(chunk.response_id)),
        );
      }
    }

    if (chunk.suggested_prompts?.length > 0) {
      dispatch(
        createSessionAction(
          setSuggestedPrompts,
          activeSession,
        )(chunk.suggested_prompts.map((display: string) => ({ display }))),
      );
    }
  };

  async function getAdditionalResources(responseId: number | undefined) {
    if (!responseId) return;

    if (!activeSession) {
      return null;
    }

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(true));
    const resourcesResponse = await axios.post('/api/office-hours/get-resources', {
      response_id: responseId,
      session_id: sessionId,
      user_id: userId,
      llm: LLM,
      language: language,
      as_superuser: superuser,
      preloaded_prompt_type: activeSession,
    });

    dispatch(incrementCostsThunk(resourcesResponse.data.cost));
    dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
    handleSetResourcesContent(resourcesResponse.data.output || resourcesContent);
  }

  /**
   * Sends a message to Lubav
   * `message` is the message as it appears in the chat box
   * `LLMInput` is the message as received by the LLM
   */
  async function sendMessage(
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    newSessionId?: number,
    requiresDocument?: DocumentType,
    skipDocumentPrompt?: boolean,
  ) {
    if (!activeSession) {
      return null;
    }

    if ((!message.trim() && !LLMInput) || !userId) return;

    hasManuallyScrolledRef.current = false;

    requiresDocument ??= typeGuardForOfficeHoursSessionType(activeSession)
      ? SESSION_TYPE_DOCUMENT_REQUIREMENTS[activeSession]
      : undefined;

    const messagesToUse = currentMessageList || officeHoursMessages;

    const tempUserMessage: OfficeHoursMessage = { text: message, sender: SENDER_TYPE.USER }; // add message without id first

    dispatch(createSessionAction(addOfficeHoursMessage, activeSession)(tempUserMessage));
    dispatch(createSessionAction(resetSuggestedPrompts, activeSession)(undefined));
    setIsTyping(true);
    setIsStreaming(true);

    try {
      if (!gotSessionResponse) {
        if (requiresDocument && !userHasConfirmedDocument && !skipDocumentPrompt) {
          const hasDocument = await checkForUserDocument(userId, requiresDocument);

          setMessagePendingDocumentUpload(LLMInput ? LLMInput : message);

          const documentData = hasDocument
            ? (
                await axios.get('/api/get-document-filename-and-url', {
                  params: {
                    user_id: userId,
                    document_type: requiresDocument,
                  },
                })
              ).data
            : {};

          if (hasDocument) {
            dispatch(
              createSessionAction(
                addOfficeHoursMessage,
                activeSession,
              )({
                text: getUploadDocumentMessage({
                  documentType: requiresDocument,
                  existingFileName: documentData.filename,
                  existingFileUrl: documentData.url,
                }),
                sender: SENDER_TYPE.AI,
                componentType: { type: 'CONFIRM_DOCUMENT', documentType: requiresDocument },
              }),
            );
          } else {
            dispatch(
              createSessionAction(
                addOfficeHoursMessage,
                activeSession,
              )({
                text: getUploadDocumentMessage({
                  documentType: requiresDocument,
                  existingFileName: documentData.filename,
                  existingFileUrl: documentData.url,
                }),
                sender: SENDER_TYPE.AI,
                componentType: { type: 'FILE_UPLOAD', documentType: requiresDocument },
              }),
            );
          }

          setIsTyping(false);

          return;
        }

        setGotSessionResponse(true);
      }

      const responseId = await fetchStreamedLubavMessage(
        '/api/office-hours/send-message',
        {
          text: LLMInput ? LLMInput : message,
          session_id: newSessionId || sessionId,
          user_id: userId,
          coaching_style: coachingStyle,
          llm: LLM,
          language: language,
          is_guidance_counselor: isGuidanceCounselor,
          as_superuser: superuser,
          as_supercoach: supercoach,
          coaching_user_id: coachingUser?.id,
          ...(gotSessionResponse || superuser || supercoach
            ? {}
            : { preloaded_prompt_type: activeSession }),
        },
        (chunk) => handleStreamChunk(chunk, messagesToUse, message, dispatch, setIsTyping),
      );

      setIsStreaming(false);

      if ((!resourcesContent || Object.keys(resourcesContent).length === 0) && responseId) {
        if (!isLoadingResources) {
          getAdditionalResources(responseId);
        }
      }
    } catch (error) {
      setIsTyping(false);
      setIsStreaming(false);
      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));

      // for now we assume an overload error if we get any error
      // TODO: handle errors on a case by case basis
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          activeSession,
        )({
          text: chatBox.overloadErr,
          sender: SENDER_TYPE.AI,
        }),
      );
      console.error(chatBox.fetchErr, error);
    }
  }

  const handleEdit = async (queryId: string) => {
    try {
      if (editingMessageId === queryId) {
        setEditingMessageId(null);
        setEditingText('');

        return;
      }

      const messageIndex = officeHoursMessages.findIndex((msg) => msg.id === queryId);
      const messageText = officeHoursMessages[messageIndex].text;

      setEditingMessageId(queryId);
      setEditingText(messageText || '');
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const shouldShowUserSelect = (message: OfficeHoursMessage, index: number): boolean => {
    return (
      message.sender === SENDER_TYPE.AI &&
      superuser &&
      (message.nuggets?.users?.length || 0) > 0 &&
      index === officeHoursMessages.length - 1
    );
  };

  const handleFileUploadSubmit = (fileName: string) => {
    setUserHasConfirmedDocument(true);
    sendMessage(
      chatBox.fileUpload.fileNameDisplay(fileName),
      messagePendingDocumentUpload,
      undefined,
      undefined,
      undefined,
      true,
    );
    setMessagePendingDocumentUpload(undefined);
  };

  const handleFileUploadError = (errorCode?: FileUploadError) => {
    dispatch(
      createSessionAction(
        updateLastMessage,
        activeSession,
      )({
        componentType: undefined,
      }),
    );
    dispatch(
      createSessionAction(
        addOfficeHoursMessage,
        activeSession,
      )({
        text:
          errorCode === 'UNAUTHORIZED'
            ? chatBox.fileUpload.unauthorizedError
            : chatBox.fileUpload.uploadError,
        sender: SENDER_TYPE.AI,
        componentType: { type: 'FILE_UPLOAD', documentType: 'resume' },
      }),
    );
  };

  if (!activeSession || !typeGuardForOfficeHoursSessionType(activeSession)) {
    return null;
  }

  return (
    <Box sx={chatboxContainer}>
      <Box sx={chatboxInnerContainer}>
        <Box ref={scrollRef} sx={chatboxStyle}>
          {superuser && (
            <UserSearch
              onClick={sendMessage}
              asSuperuser={true}
              userId={userId}
              sessionId={sessionId}
            />
          )}
          {officeHoursMessages.map((msg, index) => {
            if (!msg.text?.trim()) return null;

            /** if the message has a component type, map to it and display it */
            const getCustomComponentToDisplay = (msg: OfficeHoursMessage) => {
              if (msg.componentType?.type === 'TYPING_INDICATOR') {
                return <TypingIndicator />;
              }

              if (msg.componentType?.type === 'SELECTED_USER' && msg.componentType.user) {
                return <SelectedUser user={msg.componentType.user} />;
              }

              if (msg.componentType?.type === 'CONFIRM_DOCUMENT') {
                const documentType = msg.componentType.documentType as DocumentType;

                return (
                  <SingleSelect
                    options={
                      !userHasConfirmedDocument
                        ? [
                            chatBox.fileUpload.uploadANewDocument(documentType),
                            chatBox.fileUpload.useMyCurrentDocument(documentType),
                          ]
                        : []
                    }
                    onSelect={(option) => {
                      if (option === chatBox.fileUpload.uploadANewDocument(documentType)) {
                        dispatch(
                          createSessionAction(
                            updateLastMessage,
                            activeSession,
                          )({
                            componentType: undefined,
                          }),
                        );
                        dispatch(
                          createSessionAction(
                            addOfficeHoursMessage,
                            activeSession,
                          )({
                            text: chatBox.fileUpload.uploadUpdatedDocument(documentType),
                            sender: SENDER_TYPE.AI,
                            componentType: { type: 'FILE_UPLOAD', documentType },
                          }),
                        );
                      } else {
                        dispatch(
                          createSessionAction(
                            updateLastMessage,
                            activeSession,
                          )({
                            componentType: undefined,
                          }),
                        );
                        setUserHasConfirmedDocument(true);
                        sendMessage(
                          chatBox.fileUpload.useMyCurrentDocument(documentType),
                          messagePendingDocumentUpload,
                          undefined,
                          undefined,
                          undefined,
                          true,
                        );
                        setMessagePendingDocumentUpload(undefined);
                      }
                    }}
                  />
                );
              }

              if (msg.componentType?.type === 'FILE_UPLOAD' && messagePendingDocumentUpload) {
                return (
                  <FileUpload
                    addToProfile={true}
                    documentType={msg.componentType.documentType as DocumentType}
                    onSubmit={handleFileUploadSubmit}
                    onError={handleFileUploadError}
                  />
                );
              }

              return null;
            };

            return (
              <Box key={index}>
                <Box sx={messageRowStyle}>
                  <ChatProfilePicture sender={msg.sender} />
                  <Box sx={messageContentContainerStyle}>
                    {msg.componentType ? (
                      <Box sx={messageWithComponentStyle}>
                        {msg.text?.trim() && <MarkdownMessage>{msg.text}</MarkdownMessage>}
                        {getCustomComponentToDisplay(msg)}
                      </Box>
                    ) : (
                      <>
                        <Box
                          sx={{
                            ...messageContentStyle,
                            flexDirection: msg.sender === SENDER_TYPE.USER ? 'row' : 'column',
                            justifyContent:
                              msg.sender === SENDER_TYPE.USER ? 'space-between' : 'flex-start',
                          }}
                        >
                          <Box flex={1}>
                            {editingMessageId === msg.id ? (
                              <EditMessageBox
                                value={editingText}
                                onChange={setEditingText}
                                onSubmit={() => handleEditSubmit(msg.id ?? '', editingText)}
                              />
                            ) : (
                              <Box pr="40px">
                                <MarkdownMessage>{msg.text}</MarkdownMessage>
                              </Box>
                            )}
                          </Box>

                          {msg.sender === SENDER_TYPE.USER && (
                            <UserMessageActionsButton onEdit={() => handleEdit(msg.id || '')} />
                          )}
                          {shouldShowUserSelect(msg, index) && (
                            <UserSelect
                              contacts={msg?.nuggets?.users || []}
                              onClick={sendMessage}
                              sessionId={sessionId}
                            />
                          )}
                          {msg.sender === SENDER_TYPE.AI && (
                            <AIMessageActionsButton text={msg.text || ''} />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
          {isTyping && <TypingIndicator />}
          {!isTyping && !supercoach && (
            <SuggestedPrompts prompts={suggestedPrompts} onSelect={sendMessage} asRole={asRole} />
          )}
        </Box>
        {supercoach && (
          <Box sx={suggestedPromptsContainer}>
            <SuggestedPrompts
              prompts={suggestedPrompts}
              onSelect={sendMessage}
              asRole={asRole}
              disabled={isStreaming}
            />
          </Box>
        )}
      </Box>
      <InputBox
        sendMessage={sendMessage}
        isActive={(supercoach ? !!currentResponseId : true) && !messagePendingDocumentUpload}
        sessionId={sessionId}
      />
    </Box>
  );
};

const chatboxContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'calc(100vh - 110px)',
  gap: '10px',
  '@media (max-width: 1015px)': {
    height: 'calc(100vh - 235px)',
  },
  '@media (max-width: 900px)': {
    width: 'auto',
    height: 'calc(100vh - 200px - 50px)',
    marginRight: '2rem',
  },
};

const chatboxInnerContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: 0,
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
  ...appBorders.backrsDropShadowBorders.black,
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  '@media (max-width: 900px)': {
    width: 'auto',
    marginRight: '2rem',
  },
};

const chatboxStyle: SxProps<Theme> = {
  flexGrow: 1,
  minHeight: 0,
  overflowY: 'auto',
  borderRadius: '8px',
  padding: '15px',
  '@media (max-width: 900px)': {
    width: 'calc(100vw - 200px)',
  },
};

const messageRowStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: '11pt',
};

const messageContentContainerStyle: SxProps<Theme> = {
  wordBreak: 'break-word',
  width: '100%',
};

const messageContentStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
};

const messageActionsContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  paddingRight: '40px',
};

const iconStyle: React.CSSProperties = {
  fontSize: '16px',
};

const activeIconStyle: React.CSSProperties = {
  ...iconStyle,
  color: appColors.color.blue1,
};

const buttonStyle: SxProps<Theme> = {
  padding: '4px',
  '&:hover': {
    opacity: 0.5,
    backgroundColor: 'transparent',
  },
};

const suggestedPromptsContainer: SxProps<Theme> = {
  padding: '0px 15px 0px 15px',
  borderTop: `1px solid ${appColors.neutral.lightGray}`,
};

const messageWithComponentStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  paddingRight: '40px',
};

const linkStyle: React.CSSProperties = {
  color: appColors.color.blue1,
  fontWeight: 'bold',
};
